import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './store/store';
import { ThemeProvider } from './components/Theme/ThemeProvider';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { PublicClientApplication, EventType, AccountInfo } from '@azure/msal-browser';
import { InteractionType } from '@azure/msal-browser';
import LoadingAuthComponent from './components/Auth/LoadingAuthComponent';
import UnauthenticatedComponent from './components/Auth/UnauthenticatedComponent';
import UnauthorizedComponent from './components/Auth/UnauthorizedComponent';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { checkUserGroupAccess } from './utils/authGroupCheck';
import { setUser, setGraphJWT, setPhoto, authSelector } from './features/auth/authSlice';
import type { RootState } from './store/store';
import { openImage } from './store/chatSlice';

// Auth flow: 
// 1. MSAL auth happens here, then 
// 2. Auth slice stores user data in setUser action, then 
// 3. Auth saga detects the setUser action and automatically fetches user's profile photo from Graph API, then stores photo URL in auth slice.
// 4. Components use useSelector to get auth state (isAuthenticated, profile, photo, graphJwt) from auth slice.
// 5. Store provides auth state to all components via useSelector.

// MSAL config at top-most level of app
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29',
    authority:
      'https://login.microsoftonline.com/facac3c4-e2a5-4257-af76-205c8a821ddb',
    redirectUri: '/navigator_redirect',
    postLogoutRedirectUri: '/',
  },
  system: {
    loggerOptions: {},
    // Add other BrowserSystemOptions as needed
  },
};

const authRequest = {
  scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
};

// Function to fetch user photo from Microsoft Graph
async function fetchUserPhoto() {
  try {
    // Get the current account
    const currentAccount = msalInstance.getActiveAccount();
    if (!currentAccount) {
      console.error('No active account found');
      return;
    }

    // Acquire token specifically for Microsoft Graph
    const graphTokenRequest = {
      scopes: ['https://graph.microsoft.com/User.Read'],
      account: currentAccount,
    };

    const response = await msalInstance.acquireTokenSilent(graphTokenRequest);
    const graphToken = response.accessToken;

    // Now fetch the photo with the Graph token
    const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: {
        'Authorization': `Bearer ${graphToken}`,
      },
    });
    
    if (photoResponse.ok) {
      const blob = await photoResponse.blob();
      const photoUrl = URL.createObjectURL(blob);
      store.dispatch(setPhoto(photoUrl));
    } else {
      console.error('Failed to fetch user photo:', photoResponse.status);
    }
  } catch (error) {
    console.error('Error fetching user photo:', error);
  }
}


export const msalInstance = new PublicClientApplication(msalConfig);

const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
const AppStructure: React.FC<{ pca: PublicClientApplication }> = ({ pca }) => {
  const dispatch = useDispatch();
  const auth = useTypedSelector(authSelector);
  
  // State to track authorization status
  const [authorizationStatus, setAuthorizationStatus] = React.useState<{
    isChecking: boolean;
    isAuthorized: boolean;
    message?: string;
  }>({
    isChecking: true,
    isAuthorized: false,
  });
  
  // Check authorization on initial load for already authenticated users
  React.useEffect(() => {
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
      const groupCheck = checkUserGroupAccess(activeAccount);
      setAuthorizationStatus({
        isChecking: false,
        isAuthorized: groupCheck.hasAccess,
        message: groupCheck.message
      });
    } else {
      // No active account yet, wait for login event
      setAuthorizationStatus(prev => ({ ...prev, isChecking: true }));
    }
  }, []);

  React.useEffect(() => {
    if (auth.isJwtRequested) {
      const fn = async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) {
          console.error('Cannot find active account while calling graph');
          return;
        }
        const token = await msalInstance.acquireTokenSilent({
          account,
          scopes: ['User.Read'],
        });
        dispatch(setGraphJWT(token.accessToken));
      };
      fn();
    }
  }, [auth.isJwtRequested, dispatch]);

  React.useEffect(() => {
    if (auth.isJwtOAIRequested) {
      const fn = async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) {
          console.error('Cannot find active account while calling graph');
          return;
        }
        const tokenDto = await msalInstance.acquireTokenSilent({
          ...authRequest,
          account,
        });
        // expiresOn/extExpiresOn are Date objects, convert to string if needed
        // Also serialize account.tenantProfiles Map to avoid Redux serialization errors
        const serializedAccount = tokenDto.account?.tenantProfiles instanceof Map
          ? {
              ...tokenDto.account,
              tenantProfiles: Object.fromEntries(tokenDto.account.tenantProfiles)
            }
          : tokenDto.account;

        const tokenDtoWithStrings = {
          ...tokenDto,
          account: serializedAccount,
          expiresOn: tokenDto.expiresOn ? tokenDto.expiresOn.toISOString() : undefined,
          extExpiresOn: tokenDto.extExpiresOn ? tokenDto.extExpiresOn.toISOString() : undefined,
          requestGraph: true, // Also request graph data for photo
        };
        dispatch(setUser(tokenDtoWithStrings));
        
        // Also fetch photo for silent token acquisition
        if (tokenDto.accessToken) {
          dispatch(setGraphJWT(tokenDto.accessToken));
          fetchUserPhoto();
        }
      };
      fn();
    }
  }, [auth.isJwtOAIRequested, dispatch]);

  React.useEffect(() => {
    const handler = (e: any) => e.target.getAttribute("tag") === "imgopenai" ? dispatch(openImage(e.target.src)) : "";
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [dispatch]);

  React.useEffect(() => {
    const callbackId = msalInstance.addEventCallback((event) => {
      const state = store.getState();
      const auth = state.auth;
      
      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        console.error('login failed');
      }
      if (
        (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
          auth.isAuthenticated === false &&
          auth.profile === null) ||
        event.eventType === EventType.LOGIN_SUCCESS
      ) {
        // Only handle if payload is AuthenticationResult
        const payload = event.payload as import('@azure/msal-browser').AuthenticationResult | null;
        if (payload && 'account' in payload && payload.account) {
          // **GROUP CHECK: Verify user belongs to allowed groups**
          const groupCheck = checkUserGroupAccess(payload.account as AccountInfo);
          
          if (!groupCheck.hasAccess) {
            // User is authenticated but not authorized
            console.warn('User not authorized:', groupCheck.message);
            setAuthorizationStatus({
              isChecking: false,
              isAuthorized: false,
              message: groupCheck.message
            });
            return; // Don't proceed with app initialization
          }
          
          // User is authorized, proceed normally
          setAuthorizationStatus({
            isChecking: false,
            isAuthorized: true
          });
          
          msalInstance.setActiveAccount(payload.account as AccountInfo);
          const expiresOn = payload.expiresOn ? payload.expiresOn.toISOString() : undefined;
          const extExpiresOn = payload.extExpiresOn ? payload.extExpiresOn.toISOString() : undefined;
          
          // Serialize the account object to handle non-serializable Map values
          const serializedAccount = payload.account.tenantProfiles instanceof Map
            ? {
                ...payload.account,
                tenantProfiles: Object.fromEntries(payload.account.tenantProfiles)
              }
            : payload.account;
          
          const userPayload = { 
            ...payload, 
            expiresOn, 
            extExpiresOn, 
            requestGraph: true,
            account: serializedAccount
          };
          dispatch(setUser(userPayload));
          
          // Fetch user photo after successful login
          if (payload.accessToken) {
            dispatch(setGraphJWT(payload.accessToken));
            fetchUserPhoto();
          }
        }
      }
    });
    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, [dispatch]);

  return (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        errorComponent={UnauthenticatedComponent}
        loadingComponent={LoadingAuthComponent}
      >
        {authorizationStatus.isChecking ? (
          <LoadingAuthComponent />
        ) : authorizationStatus.isAuthorized ? (
          <ThemeProvider>
            <App />
          </ThemeProvider>
        ) : (
          <UnauthorizedComponent message={authorizationStatus.message} />
        )}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <AppStructure pca={msalInstance} />
      </Provider>
    </React.StrictMode>
  );
});

reportWebVitals();